/**
 * Capability derivation (§22c) — the single source of truth mapping a user role
 * to the catalog-manage capabilities they hold. Used two ways:
 *
 *  1. mApi forwards these to Oxbo-M as `X-Oxbo-Caps` so the SERVER can decide
 *     whether to include a catalog's `admin` block. This mirrors the live
 *     contract: the token carries the capability claim; the server gates. A
 *     field user sends no manage caps → the server omits the admin URL → it
 *     never reaches the client (cannot leak via devtools, §22c.1).
 *  2. `useCapabilities().can(...)` re-checks client-side before rendering the ↗
 *     — belt-and-suspenders to (1), never the real gate.
 *
 * In this demo model `data_admin` holds `catalog:*:write` (a wildcard); a real
 * deployment would list capability-scoped grants per §22c.4. `field` holds none.
 */
import type { UserRole } from "@app/slices/prefsSlice";

/** The wildcard a data_admin holds in the demo model. */
export const CATALOG_WILDCARD_CAP = "catalog:*:write";

/** Capabilities the role holds, as the caps list the server would receive. */
export function catalogCapabilities(role: UserRole): string[] {
  return role === "data_admin" ? [CATALOG_WILDCARD_CAP] : [];
}

/**
 * Does `caps` satisfy the required `capability`? Supports the `catalog:*:write`
 * wildcard (holder may manage any catalog) as well as exact grants.
 */
export function capsSatisfy(caps: string[], capability: string): boolean {
  if (caps.includes(capability)) return true;
  if (caps.includes(CATALOG_WILDCARD_CAP) && capability.startsWith("catalog:")) return true;
  return false;
}
